// src/frontend/hooks/useScrollAnimation.ts
import { useEffect, useState, useRef } from 'react';
import { ScrollDirection } from '@/types/settings';

interface ScrollAnimationOptions {
  enabled: 'yes' | 'no';
  threshold: number;
  direction: ScrollDirection;
}

/**
 * Hook to handle scroll-based showing/hiding of elements
 */
const useScrollAnimation = (options: ScrollAnimationOptions) => {
  // States for visibility and animation
  const [isVisible, setIsVisible] = useState<boolean>(options.enabled === 'no');
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  
  // References for tracking scroll state
  const wasVisibleRef = useRef<boolean>(false);
  const lastScrollPositionRef = useRef<number>(0);
  
  useEffect(() => {
    // If scroll animation is disabled, always show
    if (options.enabled === 'no') {
      setIsVisible(true);
      return;
    }
    
    const handleScroll = () => {
      // Get current scroll position
      const scrollPosition = window.scrollY;
      
      // Calculate document height and viewport height
      const docHeight = Math.max(
        document.body.scrollHeight, 
        document.documentElement.scrollHeight,
        document.body.offsetHeight, 
        document.documentElement.offsetHeight,
        document.body.clientHeight, 
        document.documentElement.clientHeight
      );
      const viewportHeight = window.innerHeight;
      
      // Calculate scroll percentage (0-100)
      const scrollPercentage = (scrollPosition / (docHeight - viewportHeight)) * 100;
      
      // Check if we're past the threshold - this is our trigger
      const pastThreshold = scrollPercentage >= options.threshold;
      
      // Track if we've ever passed the threshold (this becomes permanent)
      if (pastThreshold) {
        wasVisibleRef.current = true;
      }
      
      // Determine if we should show the button based on direction setting
      let shouldShow = false;
      
      if (options.direction === 'both') {
        // For 'both', once triggered, it stays visible
        shouldShow = wasVisibleRef.current;
      } else if (options.direction === 'down') {
        // For 'down', only show when past threshold
        shouldShow = pastThreshold;
      } else if (options.direction === 'up') {
        // For 'up', once triggered, only show when scrolling up or when past threshold
        if (wasVisibleRef.current) {
          // We've passed threshold at least once, so check direction
          const scrollingUp = scrollPosition < lastScrollPositionRef.current;
          shouldShow = scrollingUp || pastThreshold;
        }
      }
      
      // Store current position for next comparison
      lastScrollPositionRef.current = scrollPosition;
      
      // Update state only when visibility changes
      if (shouldShow !== isVisible) {
        if (shouldShow) {
          // When showing, trigger animation
          setShouldAnimate(true);
          setIsVisible(true);
        } else {
          // When hiding, don't animate
          setShouldAnimate(false);
          setIsVisible(false);
        }
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Check position immediately and after a brief delay (for layout)
    handleScroll();
    const timer = setTimeout(handleScroll, 100);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [options.enabled, options.threshold, options.direction, isVisible]);
  
  return { isVisible, shouldAnimate };
};

export default useScrollAnimation;